import { Provider } from '@angular/core';
import type { FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  CACHE_SIZE_UNLIMITED,
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import {
  FIREBASE_APP,
  FIREBASE_APP_CHECK,
  FIREBASE_AUTH,
  FIREBASE_FIRESTORE,
  FIREBASE_FUNCTIONS,
} from '../tokens';

export interface EmulatorEndpoint {
  host: string;
  port: number;
}

/**
 * Register an already-initialized `FirebaseApp` under {@link FIREBASE_APP}.
 *
 * The app is initialized at module scope by the caller (`initializeApp(...)`),
 * matching the previous setup and keeping it available synchronously for things
 * like the analytics bootstrap. Every other helper injects this token and passes
 * the app explicitly to the SDK (the plain SDK does not resolve an "ambient" app
 * the way `@angular/fire`'s injection-context factories did).
 */
export const provideFirebaseApp = (app: FirebaseApp): Provider[] => [
  { provide: FIREBASE_APP, useValue: app },
];

export interface FirebaseFunctionsOptions {
  region: string;
  emulator?: EmulatorEndpoint;
}

export const provideFirebaseFunctions = (options: FirebaseFunctionsOptions): Provider[] => [
  {
    provide: FIREBASE_FUNCTIONS,
    useFactory: (app: FirebaseApp) => {
      const functions = getFunctions(app, options.region);
      if (options.emulator) {
        connectFunctionsEmulator(functions, options.emulator.host, options.emulator.port);
      }
      return functions;
    },
    deps: [FIREBASE_APP],
  },
];

export interface FirebaseAppCheckOptions {
  reCaptchaSiteKey: string;
  autoRefresh: boolean;
  /** When set, registered as the App Check debug token before init (dev only). */
  debugToken?: string;
}

export const provideFirebaseAppCheck = (options: FirebaseAppCheckOptions): Provider[] => [
  {
    provide: FIREBASE_APP_CHECK,
    useFactory: (app: FirebaseApp) => {
      if (options.debugToken) {
        (globalThis as unknown as Record<string, unknown>)['FIREBASE_APPCHECK_DEBUG_TOKEN'] =
          options.debugToken;
      }
      return initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(options.reCaptchaSiteKey),
        isTokenAutoRefreshEnabled: options.autoRefresh,
      });
    },
    deps: [FIREBASE_APP],
  },
];

export interface FirebaseFirestoreOptions {
  /** Present → connect to the Firestore emulator (forces long-polling). */
  emulator?: EmulatorEndpoint;
  /** Emulator only: use an unlimited cache size. */
  cacheSizeUnlimited?: boolean;
  /** Production: enable the multi-tab persistent local cache. */
  persistentMultiTab?: boolean;
  /** Production: ignore `undefined` properties on writes. */
  ignoreUndefinedProperties?: boolean;
}

export const provideFirebaseFirestore = (options: FirebaseFirestoreOptions = {}): Provider[] => [
  {
    provide: FIREBASE_FIRESTORE,
    useFactory: (app: FirebaseApp) => {
      if (options.emulator) {
        const firestore = initializeFirestore(app, {
          // Long polling is required to reach the emulator under Cypress/Playwright.
          experimentalForceLongPolling: true,
          ...(options.cacheSizeUnlimited ? { cacheSizeBytes: CACHE_SIZE_UNLIMITED } : {}),
        });
        connectFirestoreEmulator(firestore, options.emulator.host, options.emulator.port);
        return firestore;
      }
      return initializeFirestore(app, {
        ...(options.persistentMultiTab
          ? { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) }
          : {}),
        ...(options.ignoreUndefinedProperties ? { ignoreUndefinedProperties: true } : {}),
      });
    },
    deps: [FIREBASE_APP],
  },
];

export interface FirebaseAuthOptions {
  /** Present → connect to the Auth emulator at this URL. */
  emulator?: { url: string; disableWarnings?: boolean };
}

export const provideFirebaseAuth = (options: FirebaseAuthOptions = {}): Provider[] => [
  {
    provide: FIREBASE_AUTH,
    useFactory: (app: FirebaseApp) => {
      const auth = getAuth(app);
      if (options.emulator) {
        connectAuthEmulator(auth, options.emulator.url, {
          disableWarnings: options.emulator.disableWarnings ?? false,
        });
      }
      return auth;
    },
    deps: [FIREBASE_APP],
  },
];

